import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Sprava najmu (krok FAK3): typovany klient nad `/v1/finance/rent/*`.
 * Odpovedi jsou `zod.any()` (viz `packages/finance/src/public/plugin.ts`),
 * DTO typy jsou proto psane rucne podle `plugin.ts`; request body chodi
 * z generovaneho OpenAPI kontraktu - stejny vzor jako `invoices-api.ts` (FAK2).
 */

export type PrescriptionStatus = 'active' | 'superseded' | 'ended';
export type ChargeStatus = 'planned' | 'issued' | 'partially_paid' | 'paid' | 'cancelled';
export type AllocationMethod = 'persons' | 'area' | 'equal' | 'direct';
export type SettlementStatus = 'calculated' | 'delivered' | 'settled';
export type SettlementRunStatus = 'draft' | 'calculated' | 'closed';
export type ServiceState = 'v-zalohach' | 'prevedeno' | 'sami' | 'nedodava-se';

export interface RentCustomerDto {
  readonly name: string;
  readonly ico: string | null;
  readonly dic: string | null;
  readonly street: string;
  readonly city: string;
  readonly zip: string;
  readonly email: string | null;
}

export interface PrescriptionServiceDto {
  readonly id: string;
  readonly serviceKey: string;
  readonly label: string;
  readonly advanceAmount: string;
}

export interface PrescriptionDto {
  readonly id: string;
  readonly leaseId: string | null;
  readonly partyId: string | null;
  readonly supplierId: string;
  readonly brokerUserId: string | null;
  readonly unitLabel: string;
  readonly customer: RentCustomerDto;
  readonly currency: string;
  readonly rentAmount: string;
  readonly vatRatePercent: string;
  readonly dueDay: number;
  readonly variableSymbol: string;
  readonly bankAccountIban: string | null;
  readonly personsCount: number | null;
  readonly floorAreaM2: string | null;
  readonly validFrom: string;
  readonly validTo: string | null;
  readonly status: PrescriptionStatus;
  readonly supersedesId: string | null;
  readonly note: string | null;
  readonly monthlyTotal: string;
  readonly services: readonly PrescriptionServiceDto[];
}

export interface ServiceCatalogEntryDto {
  readonly key: string;
  readonly label: string;
  readonly defaultState: ServiceState;
}

export interface BillingPreviewRowDto {
  readonly prescriptionId: string;
  readonly unitLabel: string;
  readonly customerName: string;
  readonly customerEmail: string | null;
  readonly variableSymbol: string;
  readonly dueDate: string;
  readonly rentAmount: string;
  readonly servicesAmount: string;
  readonly totalAmount: string;
  readonly alreadyCharged: boolean;
  readonly existingChargeId: string | null;
}

export interface BillingPreviewDto {
  readonly period: string;
  readonly currency: string;
  readonly rows: readonly BillingPreviewRowDto[];
  readonly plannedCount: number;
  readonly skippedCount: number;
  readonly totalAmount: string;
}

export interface BillingRunDto {
  readonly id: string;
  readonly period: string;
  readonly status: 'preview' | 'confirmed' | 'completed' | 'cancelled';
  readonly plannedCount: number;
  readonly issuedCount: number;
  readonly failedCount: number;
  readonly totalAmount: string;
  readonly currency: string;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

export interface BillingRunItemDto {
  readonly prescriptionId: string;
  readonly chargeId: string | null;
  readonly invoiceId: string | null;
  readonly status: 'created' | 'issued' | 'sent' | 'skipped' | 'failed';
  readonly error: string | null;
}

export interface BillingRunResultDto {
  readonly run: BillingRunDto;
  readonly items: readonly BillingRunItemDto[];
}

export interface ChargeDto {
  readonly id: string;
  readonly prescriptionId: string;
  readonly runId: string | null;
  readonly period: string;
  readonly dueDate: string;
  readonly currency: string;
  readonly rentAmount: string;
  readonly servicesAmount: string;
  readonly totalAmount: string;
  readonly paidAmount: string;
  readonly variableSymbol: string;
  readonly status: ChargeStatus;
  readonly invoiceId: string | null;
  readonly qrPayload: string | null;
}

export interface RentPaymentDto {
  readonly id: string;
  readonly prescriptionId: string;
  readonly chargeId: string | null;
  readonly paidOn: string;
  readonly amount: string;
  readonly currency: string;
  readonly method: 'bank' | 'cash' | 'card' | 'other';
  readonly source: 'manual' | 'myucto';
  readonly variableSymbol: string | null;
  readonly counterpartyName: string | null;
  readonly matchConfidence: 'high' | 'medium' | 'manual';
  readonly note: string | null;
}

export interface DebtorDto {
  readonly prescriptionId: string;
  readonly unitLabel: string;
  readonly customerName: string;
  readonly customerEmail: string | null;
  readonly variableSymbol: string;
  readonly dueAmount: string;
  readonly paidAmount: string;
  readonly balance: string;
  readonly daysOverdue: number;
  readonly oldestOverdueDueDate: string | null;
  readonly suggestedReminderLevel: number | null;
  readonly lastReminderLevel: number | null;
}

export interface ReminderDto {
  readonly id: string;
  readonly prescriptionId: string;
  readonly chargeId: string | null;
  readonly level: number;
  readonly amount: string;
  readonly daysOverdue: number;
  readonly sentTo: readonly string[] | null;
  readonly note: string | null;
  readonly sentAt: string | null;
}

export interface SettlementRunDto {
  readonly id: string;
  readonly label: string;
  readonly year: number;
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly status: SettlementRunStatus;
  readonly currency: string;
  readonly calculatedAt: string | null;
  readonly closedAt: string | null;
}

export interface SettlementCostDto {
  readonly id: string;
  readonly runId: string;
  readonly serviceKey: string;
  readonly label: string;
  readonly method: AllocationMethod;
  readonly totalCost: string;
  readonly note: string | null;
}

export interface SettlementLineDto {
  readonly serviceKey: string;
  readonly label: string;
  readonly method: AllocationMethod;
  readonly allocatedCost: string;
  readonly advances: string;
  readonly balance: string;
  readonly basis: string | null;
  readonly note: string | null;
}

export interface SettlementDto {
  readonly id: string;
  readonly runId: string;
  readonly prescriptionId: string;
  readonly currency: string;
  readonly totalCosts: string;
  readonly totalAdvances: string;
  readonly balance: string;
  readonly status: SettlementStatus;
  readonly deliveryDeadline: string;
  readonly evidenceDeadline: string;
  readonly deliveredOn: string | null;
  readonly objectionDeadline: string | null;
  readonly settlementDueDate: string | null;
  readonly invoiceId: string | null;
  readonly lines: readonly SettlementLineDto[];
}

export interface LeasePrefillDto {
  readonly leaseId: string;
  readonly unitId: string;
  readonly rentAmount: string;
  readonly currency: string;
  readonly startDate: string;
  readonly endDate: string | null;
  readonly tenantPartyId: string | null;
  readonly customerName: string | null;
  readonly customerEmail: string | null;
}

export interface SyncOutcomeDto {
  readonly checkedCharges: number;
  readonly importedPayments: number;
  readonly failures: ReadonlyArray<{ readonly chargeId: string; readonly error: string }>;
}

export type CreatePrescriptionBody =
  ApiPaths['/v1/finance/rent/prescriptions']['post']['requestBody']['content']['application/json'];
export type ChangePrescriptionBody =
  ApiPaths['/v1/finance/rent/prescriptions/{id}/change']['post']['requestBody']['content']['application/json'];
export type RunBillingBody =
  ApiPaths['/v1/finance/rent/billing/run']['post']['requestBody']['content']['application/json'];
export type RecordPaymentBody =
  ApiPaths['/v1/finance/rent/payments']['post']['requestBody']['content']['application/json'];
export type SetSettlementCostsBody =
  ApiPaths['/v1/finance/rent/settlement-runs/{id}/costs']['put']['requestBody']['content']['application/json'];
export type CreateSettlementRunBody =
  ApiPaths['/v1/finance/rent/settlement-runs']['post']['requestBody']['content']['application/json'];

export async function listServiceCatalog(): Promise<ServiceCatalogEntryDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/service-catalog', {}),
  )) as ServiceCatalogEntryDto[];
}

export async function listPrescriptions(
  query: { status?: PrescriptionStatus } = {},
): Promise<PrescriptionDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/prescriptions', { params: { query } }),
  )) as PrescriptionDto[];
}

export async function getPrescription(id: string): Promise<PrescriptionDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/prescriptions/{id}', { params: { path: { id } } }),
  )) as PrescriptionDto;
}

export async function createPrescription(body: CreatePrescriptionBody): Promise<PrescriptionDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/prescriptions', { body }),
  )) as PrescriptionDto;
}

export async function changePrescription(
  id: string,
  body: ChangePrescriptionBody,
): Promise<PrescriptionDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/prescriptions/{id}/change', {
      params: { path: { id } },
      body,
    }),
  )) as PrescriptionDto;
}

export async function endPrescription(id: string, validTo: string): Promise<PrescriptionDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/prescriptions/{id}/end', {
      params: { path: { id } },
      body: { validTo },
    }),
  )) as PrescriptionDto;
}

export async function prefillFromLease(leaseId: string): Promise<LeasePrefillDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/prescriptions/lease/{leaseId}/prefill', {
      params: { path: { leaseId } },
    }),
  )) as LeasePrefillDto;
}

export async function previewBilling(period: string): Promise<BillingPreviewDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/billing/preview', { params: { query: { period } } }),
  )) as BillingPreviewDto;
}

export async function runBilling(body: RunBillingBody): Promise<BillingRunResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/billing/run', { body }),
  )) as BillingRunResultDto;
}

export async function listBillingRuns(): Promise<BillingRunDto[]> {
  return (await unwrap(apiClient.GET('/v1/finance/rent/billing/runs', {}))) as BillingRunDto[];
}

export async function listCharges(
  query: { prescriptionId?: string; period?: string; unpaidOnly?: boolean } = {},
): Promise<ChargeDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/charges', { params: { query } }),
  )) as ChargeDto[];
}

export async function listRentPayments(
  query: { prescriptionId?: string; chargeId?: string } = {},
): Promise<RentPaymentDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/payments', { params: { query } }),
  )) as RentPaymentDto[];
}

export async function recordRentPayment(body: RecordPaymentBody): Promise<RentPaymentDto> {
  return (await unwrap(apiClient.POST('/v1/finance/rent/payments', { body }))) as RentPaymentDto;
}

export async function syncRentPayments(
  body: { period?: string; prescriptionId?: string } = {},
): Promise<SyncOutcomeDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/payments/sync', { body }),
  )) as SyncOutcomeDto;
}

export async function listDebtors(asOf?: string): Promise<DebtorDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/debtors', {
      params: { query: asOf === undefined ? {} : { asOf } },
    }),
  )) as DebtorDto[];
}

export async function sendRentReminder(
  prescriptionId: string,
  body: { level?: 1 | 2 | 3; note?: string } = {},
): Promise<ReminderDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/prescriptions/{id}/reminder', {
      params: { path: { id: prescriptionId } },
      body,
    }),
  )) as ReminderDto;
}

export async function listReminders(prescriptionId?: string): Promise<ReminderDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/reminders', {
      params: { query: prescriptionId === undefined ? {} : { prescriptionId } },
    }),
  )) as ReminderDto[];
}

export async function listSettlementRuns(): Promise<SettlementRunDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/settlement-runs', {}),
  )) as SettlementRunDto[];
}

export async function createSettlementRun(
  body: CreateSettlementRunBody,
): Promise<SettlementRunDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/settlement-runs', { body }),
  )) as SettlementRunDto;
}

export async function listSettlementCosts(runId: string): Promise<SettlementCostDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/settlement-runs/{id}/costs', {
      params: { path: { id: runId } },
    }),
  )) as SettlementCostDto[];
}

export async function setSettlementCosts(
  runId: string,
  body: SetSettlementCostsBody,
): Promise<SettlementCostDto[]> {
  return (await unwrap(
    apiClient.PUT('/v1/finance/rent/settlement-runs/{id}/costs', {
      params: { path: { id: runId } },
      body,
    }),
  )) as SettlementCostDto[];
}

export async function calculateSettlements(runId: string): Promise<SettlementDto[]> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/settlement-runs/{id}/calculate', {
      params: { path: { id: runId } },
      body: {},
    }),
  )) as SettlementDto[];
}

export async function closeSettlementRun(runId: string): Promise<SettlementRunDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/settlement-runs/{id}/close', {
      params: { path: { id: runId } },
    }),
  )) as SettlementRunDto;
}

export async function listSettlements(
  query: { runId?: string; prescriptionId?: string } = {},
): Promise<SettlementDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/rent/settlements', { params: { query } }),
  )) as SettlementDto[];
}

export async function deliverSettlement(id: string, deliveredOn?: string): Promise<SettlementDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/settlements/{id}/deliver', {
      params: { path: { id } },
      body: deliveredOn === undefined ? {} : { deliveredOn },
    }),
  )) as SettlementDto;
}

export async function settleSettlement(id: string): Promise<SettlementDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/rent/settlements/{id}/settle', { params: { path: { id } } }),
  )) as SettlementDto;
}
